import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppStoreService } from '@shared/services/store/app-store.service';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { NotificationService } from '@shared/services/notificacion/notification.service';
import { RealTimeService } from '@shared/services/real-time/real-time.service';
import { VideoCallService } from './video-call.service';
import {
  CallInviteEvent, CallLifecycleEvent, CallSessionDTO, CallSignalEvent, SignalType,
} from '../models/video-call.model';

export type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'active';

/**
 * STUN (Google, público) alcanza para que los dos navegadores descubran su dirección pública y
 * conecten P2P directo -- pero solo cuando el NAT de por medio lo permite. Detrás de un NAT
 * simétrico o de un firewall corporativo (frecuente en oficinas) el P2P directo nunca se
 * establece y sin más ayuda la llamada se queda "conectando" para siempre sin ningún aviso (RN:
 * era exactamente el reporte -- "no se habilita la videollamada", la pantalla de llamada se
 * abría pero jamás llegaba a 'active'). TURN es el respaldo: si el P2P directo falla, ambos
 * navegadores retransmiten el audio/video A TRAVÉS del relay en vez de uno al otro -- ya no es
 * frontend "solo señalización", pero es la única forma de que la llamada funcione en esas redes.
 * `openrelay.metered.ca` es un relay TURN gratuito de uso público (credenciales de demo
 * publicadas por el proveedor, no un secreto de este proyecto); para producción con volumen serio
 * conviene un TURN propio (coturn/Twilio/Metered de pago), pero esto ya cubre el caso que estaba roto.
 */
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'turn:openrelay.metered.ca:80', username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443', username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
];

/** Si ni P2P ni el relay TURN logran conectar en este tiempo, se corta y se avisa -- sin esto una
 *  llamada que nunca puede conectar (ambos detrás de redes muy restrictivas, o el relay caído) se
 *  queda en "conectando" indefinidamente y parece que la función está rota en vez de mostrar un error. */
const ICE_CONNECT_TIMEOUT_MS = 20_000;

/**
 * Estado y señalización WebRTC de las videollamadas 1:1, en un servicio `root` (mismo patrón que
 * `ClipRecordingService` para grabación de clips): si esto viviera en un componente de ruta, el
 * router lo destruiría al navegar a otro módulo y la llamada se cortaría sola en cuanto la
 * persona dejara el chat. Aquí sigue viva pase lo que pase con la pantalla activa; el
 * componente flotante global (`VideoCallOverlayComponent`, montado en `AppComponent`) solo LEE
 * este estado.
 *
 * <p>El servidor (Spring) nunca ve audio/video -- solo retransmite señalización (offer/answer/
 * candidatos ICE) por SSE, unos pocos KB por llamada. La conexión real es P2P entre los dos
 * navegadores vía `RTCPeerConnection`.
 */
@Injectable({ providedIn: 'root' })
export class VideoCallStateService {
  private readonly videoCallService = inject(VideoCallService);
  private readonly realTime = inject(RealTimeService);
  private readonly appStore = inject(AppStoreService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly notification = inject(NotificationService);

  private readonly currentUserId = computed(() => this.appStore.currentUser()?.id ?? null);

  readonly phase = signal<CallPhase>('idle');
  readonly call = signal<CallSessionDTO | null>(null);
  readonly otherUserId = signal<number | null>(null);
  readonly localStream = signal<MediaStream | null>(null);
  readonly remoteStream = signal<MediaStream | null>(null);
  readonly micMuted = signal(false);
  readonly cameraOff = signal(false);
  readonly mediaError = signal<string | null>(null);

  private peerConnection: RTCPeerConnection | null = null;
  private pendingIceCandidates: RTCIceCandidateInit[] = [];
  private iceConnectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.realTime.connect();
    this.realTime.on<CallInviteEvent>('call.invite').subscribe(e => this.onIncomingCall(e));
    this.realTime.on<CallSignalEvent>('call.signal').subscribe(e => void this.onSignal(e));
    this.realTime.on<CallLifecycleEvent>('call.answered').subscribe(e => void this.onAnswered(e));
    this.realTime.on<CallLifecycleEvent>('call.declined').subscribe(e => this.onRemoteEnded(e, 'declined'));
    this.realTime.on<CallLifecycleEvent>('call.ended').subscribe(e => this.onRemoteEnded(e, 'ended'));
  }

  async startCall(channelId: number): Promise<void> {
    if (this.phase() !== 'idle') return;
    const companyId = this.companyContext.companyId();
    if (!companyId) return;

    this.phase.set('outgoing');
    try {
      const call = await firstValueFrom(this.videoCallService.startCall(channelId, companyId));
      this.call.set(call);
      this.otherUserId.set(call.calleeId);
      await this.setupLocalMedia();
      this.setupPeerConnection();
      // La offer se arma recién cuando el otro contesta (onAnswered) -- no antes: si se manda
      // antes de que acepte, el ICE empieza a negociar sobre una llamada que capaz nunca contestan.
    } catch (err: unknown) {
      this.notification.showError(this.errorMessageOf(err, 'No se pudo iniciar la llamada.'));
      this.reset();
    }
  }

  /** Ignora invitaciones si ya está en otra llamada -- v1 simple: "ocupado" se resuelve
   *  rechazando la nueva de inmediato, en vez de sostener dos llamadas a la vez. */
  private onIncomingCall(event: CallInviteEvent): void {
    if (this.phase() !== 'idle') {
      this.videoCallService.declineCall(event.callId).subscribe({ error: () => { /* best-effort */ } });
      return;
    }
    const userId = this.currentUserId();
    if (!userId) return;
    this.call.set({
      id: event.callId, channelId: event.channelId, callerId: event.callerId, calleeId: userId,
      status: 'ringing', createdAt: new Date().toISOString(), answeredAt: null, endedAt: null,
    });
    this.otherUserId.set(event.callerId);
    this.phase.set('incoming');
  }

  async acceptIncomingCall(): Promise<void> {
    const call = this.call();
    if (!call || this.phase() !== 'incoming') return;
    this.phase.set('connecting');
    try {
      await this.setupLocalMedia();
      this.setupPeerConnection();
      this.startIceConnectTimeout();
      await firstValueFrom(this.videoCallService.answerCall(call.id));
    } catch (err: unknown) {
      this.notification.showError(this.errorMessageOf(err, 'No se pudo contestar la llamada.'));
      this.reset();
    }
  }

  declineIncomingCall(): void {
    const call = this.call();
    if (call) this.videoCallService.declineCall(call.id).subscribe({ error: () => {} });
    this.reset();
  }

  hangUp(): void {
    const call = this.call();
    if (call) this.videoCallService.endCall(call.id).subscribe({ error: () => {} });
    this.reset();
  }

  toggleMic(): void {
    const stream = this.localStream();
    if (!stream) return;
    const next = !this.micMuted();
    stream.getAudioTracks().forEach(t => t.enabled = !next);
    this.micMuted.set(next);
  }

  toggleCamera(): void {
    const stream = this.localStream();
    if (!stream) return;
    const next = !this.cameraOff();
    stream.getVideoTracks().forEach(t => t.enabled = !next);
    this.cameraOff.set(next);
  }

  /** Solo quien llamó arma la offer, y solo cuando el otro ya contestó -- de lo contrario los
   *  dos lados podrían mandarse offers a la vez ("glare" de WebRTC). */
  private async onAnswered(event: CallLifecycleEvent): Promise<void> {
    if (this.phase() !== 'outgoing' || this.call()?.id !== event.callId || !this.peerConnection) return;
    this.phase.set('connecting');
    this.startIceConnectTimeout();
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    this.videoCallService.sendSignal(event.callId, 'OFFER', JSON.stringify(offer)).subscribe({ error: () => {} });
  }

  private onRemoteEnded(event: CallLifecycleEvent, reason: 'declined' | 'ended'): void {
    if (this.call()?.id !== event.callId) return;
    if (reason === 'declined') this.notification.showInfo('La otra persona rechazó la llamada.');
    this.reset();
  }

  private async onSignal(event: CallSignalEvent): Promise<void> {
    if (!this.peerConnection || this.call()?.id !== event.callId) return;
    const pc = this.peerConnection;

    if (event.type === 'OFFER') {
      await pc.setRemoteDescription(new RTCSessionDescription(JSON.parse(event.payload)));
      await this.flushPendingCandidates(pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.videoCallService.sendSignal(event.callId, 'ANSWER', JSON.stringify(answer)).subscribe({ error: () => {} });
    } else if (event.type === 'ANSWER') {
      await pc.setRemoteDescription(new RTCSessionDescription(JSON.parse(event.payload)));
      await this.flushPendingCandidates(pc);
    } else if (event.type === 'ICE_CANDIDATE') {
      const candidate: RTCIceCandidateInit = JSON.parse(event.payload);
      if (pc.remoteDescription) await pc.addIceCandidate(new RTCIceCandidate(candidate));
      else this.pendingIceCandidates.push(candidate);
    }
  }

  private async flushPendingCandidates(pc: RTCPeerConnection): Promise<void> {
    for (const candidate of this.pendingIceCandidates) {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    }
    this.pendingIceCandidates = [];
  }

  private async setupLocalMedia(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      this.localStream.set(stream);
      this.mediaError.set(null);
    } catch {
      this.mediaError.set('No se pudo acceder a la cámara o al micrófono. Revisa los permisos del navegador.');
      throw new Error('media-denied');
    }
  }

  private setupPeerConnection(): void {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    this.localStream()?.getTracks().forEach(track => pc.addTrack(track, this.localStream()!));

    pc.ontrack = (ev) => {
      this.remoteStream.set(ev.streams[0]);
      this.phase.set('active');
      this.clearIceConnectTimeout();
    };
    pc.onicecandidate = (ev) => {
      const call = this.call();
      if (ev.candidate && call) {
        this.videoCallService.sendSignal(call.id, 'ICE_CANDIDATE', JSON.stringify(ev.candidate))
          .subscribe({ error: () => {} });
      }
    };
    // Sin esto, una llamada que nunca logra conectar (ninguno de los dos candidatos ICE prospera,
    // por red o por caída del relay TURN) se queda muda en 'connecting'/'active' para siempre --
    // el usuario ve la pantalla de llamada abierta sin ningún indicio de que falló. Solo se actúa
    // sobre 'failed' (terminal): 'disconnected' puede ser un corte momentáneo de red que el propio
    // ICE reintenta solo -- cortar la llamada ahí sería más agresivo que el problema que arregla.
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        this.clearIceConnectTimeout();
      } else if (pc.iceConnectionState === 'failed') {
        this.handleConnectionFailure();
      }
    };
    this.peerConnection = pc;
  }

  private startIceConnectTimeout(): void {
    this.clearIceConnectTimeout();
    this.iceConnectTimeout = setTimeout(() => this.handleConnectionFailure(), ICE_CONNECT_TIMEOUT_MS);
  }

  private clearIceConnectTimeout(): void {
    if (this.iceConnectTimeout) {
      clearTimeout(this.iceConnectTimeout);
      this.iceConnectTimeout = null;
    }
  }

  private handleConnectionFailure(): void {
    if (this.phase() === 'idle' || this.phase() === 'active') return;
    const call = this.call();
    if (call) this.videoCallService.endCall(call.id).subscribe({ error: () => {} });
    this.notification.showError('No se pudo conectar la videollamada. Revisa tu conexión e intenta de nuevo.');
    this.reset();
  }

  private errorMessageOf(err: unknown, fallback: string): string {
    const httpError = err as { error?: { message?: string } } | undefined;
    return httpError?.error?.message ?? fallback;
  }

  private reset(): void {
    this.clearIceConnectTimeout();
    this.peerConnection?.close();
    this.peerConnection = null;
    this.localStream()?.getTracks().forEach(t => t.stop());
    this.localStream.set(null);
    this.remoteStream.set(null);
    this.pendingIceCandidates = [];
    this.call.set(null);
    this.otherUserId.set(null);
    this.phase.set('idle');
    this.micMuted.set(false);
    this.cameraOff.set(false);
    this.mediaError.set(null);
  }
}
